import { useState } from 'react';
import { Anchor, Button, Card, Center, Stack, Text } from '@mantine/core';
import { useSession } from '../lib/store';

/**
 * Renders when an authenticated session has resolved but the user has no
 * current account (`currentAccountId === null`).
 *
 * Shell: any — mounted by `RequireSubscription` (`router.tsx`), before
 * `DesktopLayout` or any shell chrome exists to lean on.
 *
 * ## Who actually lands here — corrected 2026-08-23 (vendor#1015)
 *
 * This docblock used to say:
 *
 * > "the `handle_new_user` trigger guarantees every signup a seat, so this
 * > branch should be unreachable in practice"
 *
 * **That has been false since `20261012091000_no_account_for_invited_users`,
 * which made this branch reachable on purpose.** `handle_new_user` now returns
 * early — creating the `public.users` row but no account — when the new user is
 * an invitee, which it decides from `auth.users.invited_at IS NOT NULL` or a
 * live `account_invites` row matching their address. That migration's own
 * header flagged the consequence and asked for exactly this check:
 *
 * > "A person who signs up against a pending invite and then never accepts it
 * > now has NO account at all … the empty-account rendering path should be
 * > confirmed before this is relied on in front of customers."
 *
 * It was not confirmed, and the answer was that it was a dead end: a screen
 * headed "No account found" whose only affordance was Sign out.
 *
 * **The correction matters beyond the wording.** Because the trigger gives an
 * account to every signup that is NOT an invitee, being an invitee is very
 * nearly the only way to arrive here — so leading with the invitation is the
 * accurate reading of this state, not a hedge. The exit stays for the genuine
 * edge cases.
 *
 * ## Why it does not name the account
 *
 * It cannot, and deliberately. `public.account_invites` has RLS enabled with
 * **zero policies** and grants only to `postgres` and `service_role`, so
 * `authenticated` cannot read a row of it under any predicate — and
 * `docs/multi-seat-accounts.md` §5.2 rules that there is
 * *"deliberately no invite-preview endpoint — a 'who invited me?' screen
 * readable before login is an enumeration oracle."*
 *
 * An RPC scoped to the caller's own **confirmed** address would name the
 * account without being readable before login and without being an oracle
 * (it takes no arguments, so there is nothing to enumerate over), and one is
 * written and tested — but that is a change to a standing security ruling and
 * belongs to the owner, not to this PR. Proposed on vendor#1015.
 *
 * ## Non-obvious contracts
 *
 *   - **The invitation email is the only door, and that is the design.** §5.2:
 *     the token is never stored (only `token_hash`), the plaintext leaves the
 *     database exactly once, and `accept_account_invite` is two-factor —
 *     possession of the token AND a confirmed address. So "open the link in
 *     your email" is not a fob-off; it is the whole mechanism.
 *   - The confirm-your-address line is there because that second factor fails
 *     silently otherwise: `accept_account_invite` collapses six causes,
 *     unconfirmed address among them, into one `invalid or expired invite`.
 *   - Uses `window.location.replace` rather than `useNavigate` — matches the
 *     sibling post-checkout recovery branch already in `RequireSubscription`
 *     (the "Activating your subscription…" retry button), which does a hard
 *     reload rather than a client-side navigation once session state is this
 *     degraded.
 */
export function NoAccountFallback() {
  const signOut = useSession((s) => s.signOut);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    window.location.replace('/sign-in');
  };

  return (
    <Center style={{ minHeight: '100vh' }}>
      <Stack align="center" gap="md" maw={460} p="xl">
        <Stack align="center" gap={6}>
          <Text fw={600} size="lg">
            One step left to join
          </Text>
          <Text c="dimmed" size="sm" ta="center" style={{ textWrap: 'pretty' }}>
            You’re signed in, but you’re not on an account yet — which is what happens when someone
            has invited you and the invitation hasn’t been accepted.
          </Text>
        </Stack>

        <Card
          withBorder
          radius="var(--radius-card, 12px)"
          p="md"
          w="100%"
          style={{ background: 'var(--surface-raised)' }}
          data-testid="no-account-invite-help"
        >
          <Stack gap={6}>
            <Text fw={600} size="sm">
              Open the link in your invitation email
            </Text>
            <Text c="dimmed" size="sm" style={{ textWrap: 'pretty' }}>
              That link is the only way to accept — we never store it, so we can’t re-send it from
              here. Ask whoever invited you for a fresh one if it has expired.
            </Text>
            <Text c="dimmed" size="sm" style={{ textWrap: 'pretty' }}>
              If you signed up just now, confirm your email address first. Accepting needs both the
              link and a confirmed address.
            </Text>
          </Stack>
        </Card>

        <Text c="dimmed" size="xs" ta="center" style={{ textWrap: 'pretty' }}>
          Weren’t expecting an invitation? Sign out and try again, or email{' '}
          <Anchor size="xs" href="mailto:support@mytcgbinder.com">
            support@mytcgbinder.com
          </Anchor>
          .
        </Text>

        <Button
          variant="default"
          loading={signingOut}
          onClick={() => void handleSignOut()}
          data-testid="no-account-sign-out"
        >
          Sign out
        </Button>
      </Stack>
    </Center>
  );
}
